import axiosInstance from "api/axiosInstance";

// 카카오 SDK 타입 정의
declare global {
  interface Window {
    Kakao: any;
  }
}

const KAKAO_JS_KEY = process.env.REACT_APP_KAKAO_JS_KEY;

/**
 * 카카오 OAuth redirect URI
 * 카카오 콘솔에 등록된 값과 byte-exact 일치해야 함
 */
export const KAKAO_REDIRECT_URI = `${window.location.origin}/auth/kakao/callback`;

const STORAGE_KEY_STATE = "kakaoAuthState";
const STORAGE_KEY_INTENT = "kakaoAuthIntent";

/**
 * 카카오 인증 진입 의도
 * - signup-ymyd: 약문약답 회원 회원가입 흐름
 * - login: 일반 로그인 (향후 확장 대비)
 */
export type KakaoAuthIntent = "signup-ymyd" | "login";

/**
 * UUID v4 생성 (CSRF state 토큰용)
 *
 * crypto.randomUUID()는 Secure Context(HTTPS 또는 localhost)에서만 동작.
 * LAN IP(http://192.168.x.x) 등 insecure context에서는 crypto.getRandomValues로 fallback.
 */
function generateState(): string {
  if (typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback: crypto.getRandomValues로 RFC4122 v4 UUID 생성
  // (insecure context에서도 사용 가능)
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  // version 4
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  // variant
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20)}`
  );
}

/**
 * 카카오 SDK 초기화
 * 환경변수 미설정 시 호출 시점에 throw (앱 전체가 깨지지 않도록 lazy 검증)
 */
export const initKakaoSDK = (): void => {
  if (!KAKAO_JS_KEY) {
    throw new Error(
      "REACT_APP_KAKAO_JS_KEY 환경변수가 설정되지 않았습니다."
    );
  }
  if (window.Kakao && !window.Kakao.isInitialized()) {
    window.Kakao.init(KAKAO_JS_KEY);
  }
};

/**
 * 카카오 인증 페이지로 redirect
 * - PC: 카카오 계정 로그인 페이지로 이동
 * - 모바일(카톡 설치): 카카오톡 앱 자동 호출 후 복귀
 *
 * @param intent 진입 의도 (sessionStorage에 저장되어 콜백 페이지에서 복원됨)
 */
export const startKakaoAuthorize = (intent: KakaoAuthIntent): void => {
  initKakaoSDK();
  if (!window.Kakao) {
    throw new Error("Kakao SDK가 로드되지 않았습니다.");
  }

  // CSRF 방지용 state — sessionStorage에 저장 후 콜백에서 검증
  const state = generateState();
  sessionStorage.setItem(STORAGE_KEY_STATE, state);
  sessionStorage.setItem(STORAGE_KEY_INTENT, intent);

  window.Kakao.Auth.authorize({
    redirectUri: KAKAO_REDIRECT_URI,
    state,
  });
};

/**
 * 콜백 페이지에서 sessionStorage의 state/intent를 읽고 즉시 삭제
 * 한 번만 사용되도록 일회성 패턴
 */
export const consumeKakaoAuthState = (): {
  state: string | null;
  intent: KakaoAuthIntent | null;
} => {
  const state = sessionStorage.getItem(STORAGE_KEY_STATE);
  const intent = sessionStorage.getItem(
    STORAGE_KEY_INTENT
  ) as KakaoAuthIntent | null;
  sessionStorage.removeItem(STORAGE_KEY_STATE);
  sessionStorage.removeItem(STORAGE_KEY_INTENT);
  return { state, intent };
};

/**
 * 인가코드를 백엔드에 POST하여 kakaoId 수신
 * 카카오 access_token은 백엔드에서 즉시 폐기되며 프론트로 전달되지 않음
 */
export const exchangeKakaoCode = async (
  code: string
): Promise<{ kakaoId: string }> => {
  const res = await axiosInstance.post("/api/v1/auth/kakao/exchange", {
    code,
    redirectUri: KAKAO_REDIRECT_URI,
  });
  if (res.data.error !== 0) {
    throw new Error(res.data.message || "카카오 인증 실패");
  }
  return res.data.data;
};

/**
 * 카카오 로그아웃 (v2 Promise 기반)
 * 현재는 외부 호출처 없으나 향후 사용 대비 호환용으로 유지
 */
export const handleKakaoLogout = async (): Promise<void> => {
  if (window.Kakao?.Auth?.getAccessToken?.()) {
    await window.Kakao.Auth.logout();
  }
};
